export type CoreReference = { kind: string; id: string; title: string; url?: string; snapshotId?: string };
export type CoreResult = { responseId: string; text: string; attachmentSummary: string | null; references: CoreReference[] };
export type CoreActivity = { id: string; label: string; state: "running" | "complete" | "error" };
export type CoreStreamEvent =
  | { type: "activity"; activity: CoreActivity }
  | { type: "text"; delta: string }
  | { type: "reasoning-summary"; id: string; delta: string }
  | { type: "references"; references: CoreReference[] }
  | { type: "complete"; result: CoreResult }
  | { type: "error"; message: string };
export type CoreEmitter = (event: CoreStreamEvent) => void;

/** Decode SSE across arbitrary byte/UTF-8/line boundaries. Ignore comments and new event types. */
export async function* readSSE(body: ReadableStream<Uint8Array>, signal?: AbortSignal): AsyncGenerator<unknown> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  const cancel = () => { void reader.cancel().catch(() => {}); };
  signal?.addEventListener("abort", cancel, { once: true });
  try {
    while (true) {
      signal?.throwIfAborted();
      const { value, done } = await reader.read();
      signal?.throwIfAborted();
      buffer += decoder.decode(value, { stream: !done });
      let match: RegExpExecArray | null;
      while ((match = /\r?\n\r?\n/.exec(buffer))) {
        const frame = buffer.slice(0, match.index);
        buffer = buffer.slice(match.index + match[0].length);
        const data = frame.split(/\r?\n/).filter(line => line.startsWith("data:")).map(line => line.slice(5).replace(/^ /, "")).join("\n");
        if (data && data !== "[DONE]") yield JSON.parse(data);
      }
      if (buffer.length > 2_000_000) throw new Error("The response stream exceeded its event limit.");
      if (done) break;
    }
    if (buffer.trim() && !buffer.trim().startsWith(":")) throw new Error("The response stream ended unexpectedly.");
  } finally {
    signal?.removeEventListener("abort", cancel);
    await reader.cancel().catch(() => {});
    reader.releaseLock();
  }
}

const labels: Record<string, string> = {
  search: "Searching the workspace", list_sources: "Finding sources", get_source: "Reading a source",
  list_systems: "Finding systems", get_system: "Examining a system", list_simulations: "Finding simulations",
  get_simulation: "Reading a simulation", list_projections: "Finding projections", get_projection: "Reading a projection",
  list_portfolio: "Reviewing portfolio records", list_signals: "Finding recent signals", get_signal: "Reading a signal",
  find_claims: "Checking claims", trace_signal: "Tracing the evidence", get_state: "Reviewing the workspace",
  get_world_model: "Reading the saved World Model", get_world_model_node: "Inspecting a World Model connection",
  get_decision_engine: "Reviewing saved market decisions and execution state",
};
export function coreToolLabel(name: string) { return labels[name.replace(/^asymmetric_/, "")] || "Reading workspace records"; }
